import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, Button, ButtonProps, Center, Fade, Flex, Heading } from '@chakra-ui/react';
import { pureCleanMeetingData, pureRestartMeeting } from '@/backend/pureRestartMeeting';
import LiveMeetingInteractionsMenu from '@/components/LiveMeetingInteractionsMenu/LiveMeetingInteractionsMenu';
import MeetingList from '@/components/MeetingList/MeetingList';
import getAvailableUserMeetings from '@/features/meeting/helpers/getAvailableUserMeetings';
import { trackPageView } from '@/features/tracking/trackingHelpers';
import { ROUTES } from '@/routes/Routes';
import { Meeting } from '@/services/API';
import useMeetingUserAccessForUser from '@/services/hooks/useMeetingUserAccessForUser';
import { useUserMeetingPing } from '@/services/hooks/useUserMeetingPing';
import useMeetingSubscription from '@/services/subscriptions/useMeetingSubscription';
import { MeetingSelectors } from '@/store/slices/meeting';
import { useAppDispatch, useAppSelector } from '@/store/StoreHooks';
import { fetchMeetingList } from '@/store/thunk/meeting';
import PageLayout from '@/ui/Layout/PageLayout';
import PopConfirm from '@/ui/PopConfirm/PopConfirm';
import { goToLiveMeetingPage } from '@/utils/helpers/goToLiveMeetingPage';
import { useUserAuthenticationContext } from '../userAuth/context/UserAuthenticationContext';
import useDoStatsExistForMeetings from './answer/hooks/useDoStatsExistForMeetings';
import { getMeetingStatus, MEETING_STATUS } from './helpers/getMeetingStatus';

// --------------------- styled ------------------------

function MeetingListView() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user, isAdmin, userClients } = useUserAuthenticationContext();
  const { t } = useTranslation();
  // NOTE: we set the id of a meeting here when we want the action btn to be in a loading state
  const [meetingLoadingID, setMeetingLoadingID] = useState<string | null>(null);

  const { userAccessList } = useMeetingUserAccessForUser(user?.id);
  // recover full meeting list
  const originalMeetingList = useAppSelector(MeetingSelectors.getMeetingList);

  const meetingList = useMemo(() => {
    if (!originalMeetingList || !user) return [];
    let toReturn = originalMeetingList.filter((meeting) => meeting.isArchived === false);
    toReturn = getAvailableUserMeetings(toReturn, user, userClients, userAccessList, isAdmin);
    return toReturn;
  }, [originalMeetingList, userAccessList.length, user, userClients]);

  // filter to use only available meetings

  const statsExistForMeeting = useDoStatsExistForMeetings(meetingList);

  useMeetingSubscription();

  // --------------------- methods ------------------------

  // handle meeting join click
  const handleMeetingJoin = async (meetingID: string, testMode: boolean = false) => {
    goToLiveMeetingPage({
      meetingID,
      testMode,
    });
  };

  const handleMeetingStats = (meetingID: string) => {
    navigate(`${ROUTES.MEETING_STATS}/${meetingID}`);
  };

  // --------------------- effects ------------------------

  // ping user
  // TODO: merge this with auto checking network status
  useUserMeetingPing(user);

  // fetch meetings
  useEffect(() => {
    dispatch(fetchMeetingList());
  }, [dispatch]);

  // track
  useEffect(() => {
    if (user?.id) {
      trackPageView({ pageUrl: 'home', pageTitle: 'home', userId: user?.id });
    }
  }, [user?.id]);

  // SIMPLE auto connect
  /*
  NOTE: Auto connect
  Logic:
  1 - From all the meetings being displayed to the user, take all the ones taking place today
  2 - if there is only one and the user is not the host, and is not an admin
  3 - This will make it work even when there are two meetings for example in the list but only 1 is today 
  */
  useEffect(() => {
    const today = new Date().toDateString();

    const meetingsToday = meetingList.filter((meeting) => {
      const eventDate = new Date(meeting.eventDate).toDateString();
      return today === eventDate;
    });
    if (meetingsToday.length === 1) {
      const meetingToJoin = meetingsToday[0];

      if (!isAdmin && meetingToJoin.host?.id !== user?.id) handleMeetingJoin(meetingToJoin.id);
    }
  }, [meetingList]);

  // --------------------- render ------------------------

  return (
    <PageLayout>
      <Box p="20px" bg="livelinx.grey100" h="100%">
        {user && meetingList.length == 0 && (
          <Fade in={true} delay={1}>
            <Box h="full">
              <Center h="100px">
                <Heading size="sm" color="grey">
                  {t('home.noMeeting')}
                </Heading>
              </Center>
            </Box>
          </Fade>
        )}

        {meetingList.length > 0 && user && (
          <Fade in={true}>
            <MeetingList
              data={meetingList}
              showGuestUrl={isAdmin}
              actions={[
                {
                  render: (item: Meeting) => {
                    const doStatsExistForThisMeeting = statsExistForMeeting[item.id];
                    const status = getMeetingStatus(item);
                    const isHost = item?.host?.id === user.id;

                    // main button
                    const mainButtonVisible = [
                      MEETING_STATUS.DEMO,
                      MEETING_STATUS.READY,
                      MEETING_STATUS.TESTING,
                      MEETING_STATUS.STARTED,
                    ].includes(status);

                    const startDemoButton: ButtonProps = {
                      children: t('admin.home.table.startDemo.button'),
                      variant: 'ghost',
                      onClick: async () => {
                        setMeetingLoadingID(item.id);
                        await pureCleanMeetingData(item.id);
                        await handleMeetingJoin(item.id);
                        setMeetingLoadingID(null);
                      },
                    };
                    const joinDemoButton: ButtonProps = {
                      children: t('admin.home.table.joinDemo.button'),
                      variant: 'ghost',
                      onClick: async () => {
                        await handleMeetingJoin(item.id);
                      },
                    };
                    const startTestingButton: ButtonProps = {
                      children: t('admin.home.table.startTesting.button'),
                      variant: 'primary',
                      onClick: async () => {
                        setMeetingLoadingID(item.id);
                        await pureCleanMeetingData(item.id);
                        await handleMeetingJoin(item.id, true);
                        setMeetingLoadingID(null);
                      },
                    };
                    const joinTestingButton: ButtonProps = {
                      children: t('admin.home.table.joinTesting.button'),
                      variant: 'primary',
                      onClick: async () => {
                        await handleMeetingJoin(item.id);
                      },
                    };

                    const startMeeting: ButtonProps = {
                      children: t('admin.home.table.startMeeting.button'),
                      variant: 'danger',
                      // no on click as we do this in the pop confirm
                      // onClick: async() =>{
                      // }
                    };
                    const joinMeetingButton: ButtonProps = {
                      children: t('admin.home.table.joinMeeting.button'),
                      variant: 'primary',
                      onClick: async () => {
                        await handleMeetingJoin(item.id);
                      },
                    };

                    const currentButtonProps = (() => {
                      if (status == MEETING_STATUS.DEMO)
                        return isHost || isAdmin ? startDemoButton : joinDemoButton;
                      if (status == MEETING_STATUS.TESTING)
                        return isHost || isAdmin ? startTestingButton : joinTestingButton;
                      if (status == MEETING_STATUS.READY && isAdmin) return startMeeting;
                      // default
                      return joinMeetingButton;
                    })();

                    const startAfterConfirmation = async () => {
                      await pureRestartMeeting(item.id);
                      await handleMeetingJoin(item.id);
                    };

                    return (
                      <Flex direction="column" gap={2} width="150px">
                        {isAdmin && status === MEETING_STATUS.READY ? (
                          <PopConfirm
                            title="You will now start the Event meeting. Are you sure?" // TODO: translate
                            onConfirm={startAfterConfirmation}
                          >
                            <Button
                              variant="primary"
                              isDisabled={!mainButtonVisible}
                              size="md"
                              w="100%"
                              isLoading={meetingLoadingID === item.id}
                              {...currentButtonProps}
                            />
                          </PopConfirm>
                        ) : (
                          <Button
                            variant="primary"
                            size="md"
                            isDisabled={!mainButtonVisible}
                            w="100%"
                            isLoading={meetingLoadingID === item.id}
                            {...currentButtonProps}
                          />
                        )}

                        {isAdmin && (
                          <>
                            <LiveMeetingInteractionsMenu meeting={item} />
                          </>
                        )}

                        {isHost && (
                          <>
                            {doStatsExistForThisMeeting && (
                              <Button
                                variant="secondary"
                                size="xs"
                                onClick={() => {
                                  handleMeetingStats(item.id);
                                }}
                              >
                                {t('admin.home.table.meetingStats.button')}
                              </Button>
                            )}
                          </>
                        )}
                      </Flex>
                    );
                  },
                },
              ]}
            />
          </Fade>
        )}
      </Box>
    </PageLayout>
  );
}

export default MeetingListView;
